import { useState, useCallback } from 'react';
import { routeQueryToTools, sendMessage, type ChatMessage, type AgentConfig } from '../services/agent';
import { lookupFaqCache, shouldCacheQuestion, storeFaqCache } from '../services/faqCache';

function buildCacheToolCall(source: 'local' | 'shared', routeKind: string, cachedAt: string) {
  return [{
    name: source === 'local' ? 'faq_cache_local' : 'faq_cache_shared',
    args: { routeKind },
    result: {
      tool: source === 'local' ? 'faq_cache_local' : 'faq_cache_shared',
      result: {
        routeKind,
        cachedAt,
      },
    },
  }];
}

export function useChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiKey = import.meta.env.VITE_ANTHROPIC_API_KEY || '';

  const send = useCallback(async (userMessage: string, context?: { page?: string; entityId?: string }) => {
    if (!apiKey) {
      setError('Anthropic API key not configured. Set VITE_ANTHROPIC_API_KEY in .env');
      return;
    }

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: context
        ? `[Context: User is on ${context.page}${context.entityId ? `, viewing entity ${context.entityId}` : ''}]\n\n${userMessage}`
        : userMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);
    setError(null);

    try {
      const route = routeQueryToTools(userMessage);
      const allowFaqCache = shouldCacheQuestion(userMessage);

      if (allowFaqCache) {
        const cachedAnswer = await lookupFaqCache(userMessage, route.kind);

        if (cachedAnswer) {
          const assistantMsg: ChatMessage = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: cachedAnswer.answer,
            toolCalls: buildCacheToolCall(cachedAnswer.source, cachedAnswer.routeKind, cachedAnswer.lastUsedAt),
            timestamp: new Date(),
          };

          setMessages(prev => [...prev, assistantMsg]);
          return;
        }
      }

      const config: AgentConfig = { apiKey };
      const allMessages = [...messages, userMsg];
      const { response, toolCalls } = await sendMessage(allMessages, config);

      if (allowFaqCache) {
        await storeFaqCache({
          question: userMessage,
          routeKind: route.kind,
          answer: response,
          toolNames: toolCalls?.map((toolCall) => toolCall.name) || [],
        });
      }

      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: response,
        toolCalls: toolCalls || undefined,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMsg]);
    } catch (err: any) {
      setError(err.message);
      const errorMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `Sorry, I encountered an error: ${err.message}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, apiKey]);

  const clear = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return { messages, isLoading, error, send, clear, hasApiKey: !!apiKey };
}
